import 'react-datepicker/dist/react-datepicker.css'
import React, { useEffect, useCallback, useState, useRef } from 'react'
import { Alert, Button, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import moment from 'moment-timezone'
import TelephoneInput from 'react-phone-number-input/input'
import { subYears } from 'date-fns'
import Select from 'react-select'

import { useCurrentUser } from '../../hooks/useCurrentUser'
import useApi from '../../hooks/useApi'
import AvatarImage from './avatar-image'
import CausePicker from '../../components/CausePicker/CausePicker'
import DefaultCharity from '../../components/Charity/DefaultCharity'
import Icon from '../../components/icon'
import Loading from '../../components/loading'
import LocationSearch from '../../components/location-search'
import PasswordInput from '../../components/password-input'
import CivWallet from '../../components/CivWallet/civ-wallet'
import {
  formatPhoneNumberForDisplay,
  formatPhoneNumberForSave,
} from '../../lib/phone'
import { ensureAbsoluteUrl } from '../../utils/url'

registerLocale('es', es)
setDefaultLocale('en')

export default function AccountEditPage() {
  const { i18n, t } = useTranslation()

  const { currentUser, locale, setCurrentUser, fetchUser, hasBetaFeature } =
    useCurrentUser()
  const {
    getCurrentUser,
    updateAccount,
    updatePassword,
    updateUser,
    updateUserInformation,
    updateEmailHeaders,
    requestAccountVerification,
    sendAccountVerificationCode,
    getShirtSizes,
    getSkills,
    getCommunityContributions,
    usernameAvailable,
  } = useApi()

  const [confirmPassword, setConfirmPassword] = useState()
  const [hasUserChanged, setHasUserChanged] = useState(false)
  const [openPasswordConfirmationModal, setOpenPasswordConfirmationModal] =
    useState(false)
  const [emailPasswordConfirmation, setEmailPasswordConfirmation] = useState()

  const [isLoading, setIsLoading] = useState(false)
  const [newPassword, setNewPassword] = useState()
  const [user, setUser] = useState(() => currentUser)
  const [email, setEmail] = useState(() => currentUser.email)

  const [waitingForEmailVerification, setWaitingForEmailVerification] =
    useState(false)
  const [emailVerificationCode, setEmailVerificationCode] = useState('')
  const [emailVerificationError, setEmailVerificationError] = useState(false)
  const [emailVerificationMaxPerDay, setEmailVerificationMaxPerDay] =
    useState(false)
  const [emailVerificationMaxPerMonth, setEmailVerificationMaxPerMonth] =
    useState(false)
  const [shirtSizes, setShirtSizes] = useState()
  const nameRef = useRef(null)
  const [checkingNickname, setCheckingNickname] = useState(false)
  const [nicknameTaken, setNicknameTaken] = useState(false)
  const nicknameCheckTimer = useRef(null)
  const [hasSocialsChanged, setHasSocialsChanged] = useState(false)
  const [isSavingSocials, setIsSavingSocials] = useState(false)
  const [skillsList, setSkillsList] = useState([])
  const [communityContributionsList, setCommunityContributionsList] = useState([])

  const fetchData = useCallback(async () => {
    const sizes = await getShirtSizes()
    setShirtSizes(sizes.data)
    const [skillsRes, communityContributionsRes, response] = await Promise.all([
      getSkills(),
      getCommunityContributions(),
      getCurrentUser(),
    ])
    const skillsData = skillsRes?.data ?? skillsRes
    const ccData = communityContributionsRes?.data ?? communityContributionsRes
    setSkillsList(Array.isArray(skillsData) ? skillsData : [])
    setCommunityContributionsList(Array.isArray(ccData) ? ccData : [])
    const { data } = response
    if (data) {
      if (data.gender === null) {
        data.gender = ''
      }
      if (data.location === null) {
        data.location = ''
      }
      if (data.school === null || data.school === undefined) {
        data.school = ''
      }
      if (data.school_type === null || data.school_type === undefined) {
        data.school_type = ''
      }
      data.skill_ids = data.skills?.map(s => s.id) ?? []
      data.community_contribution_ids = data.community_contributions?.map(c => c.id) ?? []
      setUser(data)
    }
    setIsLoading(false)
  }, [getCurrentUser, getSkills, getCommunityContributions])

  const onLocationChange = useCallback(
    async _location => {
      setUser(user => ({
        ...user,
        location: _location?.description,
      }))
      setHasUserChanged(true)
    },
    [user],
  )

  const onSchoolChange = useCallback(
    async _location => {
      setUser(user => ({
        ...user,
        school: _location?.description ?? '',
      }))
      setHasUserChanged(true)
    },
    [user],
  )

  const onChangeNickname = useCallback(
    async value => {
      setUser(user => ({
        ...user,
        nickname: value,
      }))
      setHasUserChanged(true)

      // Clear existing timer
      if (nicknameCheckTimer.current) {
        clearTimeout(nicknameCheckTimer.current)
      }

      setCheckingNickname(false)
      setNicknameTaken(false)

      const trimmedValue = value.trim()
      if (!trimmedValue || trimmedValue === currentUser?.nickname) {
        return
      }

      nicknameCheckTimer.current = setTimeout(async () => {
        setCheckingNickname(true)
        try {
          const response = await usernameAvailable({ username: trimmedValue })
          const available = !!(response?.data?.available)
          setNicknameTaken(!available)
        } catch (error) {
          setNicknameTaken(true)
        } finally {
          setCheckingNickname(false)
        }
      }, 500)
    },
    [currentUser?.nickname, usernameAvailable],
  )

  const onReturnUrl = () => {
    if (localStorage.getItem('return-url')) {
      const url = localStorage.getItem('return-url')
      localStorage.removeItem('return-url')
      window.location = url
    }
  }

  const onEmailVerifyRequest = useCallback(async () => {
    const response = await requestAccountVerification()
    if (response.status === 200) {
      setWaitingForEmailVerification(true)
      setEmailVerificationError(false)
      setEmailVerificationCode('')
    } else {
      const { data } = response.error.response
      if (data.base && data.base[0].includes('max_per_day')) {
        setEmailVerificationMaxPerDay(true)
      } else if (data.base && data.base[0].includes('max_per_month')) {
        setEmailVerificationMaxPerMonth(true)
      }
    }
  })

  const onEmailVerifyCode = useCallback(async () => {
    const response = await sendAccountVerificationCode(emailVerificationCode)
    if (response.status === 200) {
      setWaitingForEmailVerification(false)
      onReturnUrl()
      if (localStorage.getItem('verify-email-message')) {
        localStorage.removeItem('verify-email-message')
        setCurrentUser({ ...currentUser, email_verified_at: new Date() })
        window.location = '/charities'
      } else {
        fetchData()
      }
    } else {
      setEmailVerificationError(true)
    }
  }, [emailVerificationCode, currentUser])

  const onEmailVerifyCancel = useCallback(async () => {
    setWaitingForEmailVerification(false)
    localStorage.removeItem('verify-email-message')
    onReturnUrl()
  })

  const onSaveNewPassword = useCallback(
    async e => {
      setIsLoading(true)
      e.preventDefault()
      const response = await updatePassword({
        newPassword,
        confirmPassword,
      })
      if (response.errors) {
        // eslint-disable-next-line no-alert
        alert(response.errors?.full_messages?.[0])
      } else {
        setNewPassword('')
        setConfirmPassword('')
      }
      setIsLoading(false)
    },
    [user, newPassword, confirmPassword],
  )

  const onSave = useCallback(async () => {
    if (user.school?.trim() && !user.school_type) {
      alert(t('users.edit.school_type_required'))
      return
    }
    setIsLoading(true)
    const params = {
      birthday: user.birthday
        ? moment(user.birthday).format('YYYY-MM-DD 12:00')
        : undefined,
      gender: user.gender,
      shirt_size_id: user.shirt_size_id,
      language: user.language,
      location: user.location,
      marketing_opt_in: user.marketing_opt_in,
      media_attachment_id: user.media_attachment_id,
      name: user.name,
      phone: user.phone ? formatPhoneNumberForSave(user.phone) : '',
      work_title: user.work_title,
      work_place: user.work_place,
      school: user.school ?? '',
      school_type: user.school_type ?? '',
    }

    const formData = new FormData()
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined) {
        formData.append(key, params[key])
      }
    })


    const formDataEntries = {}
    formData.forEach((value, key) => { formDataEntries[key] = value })

    const response = await updateAccount(formData)

    if (response.errors) {
      // eslint-disable-next-line no-alert
      alert(
        response.errors?.full_messages?.[0] ??
          userInformationResponse.errors?.full_messages?.[0],
      )
    } else {
      if (user.language) {
        i18n.changeLanguage(user.language)
        moment.locale(user.language || locale)
      }
      setHasUserChanged(false)
    }


    if (user.id) {
      try {
        const userUpdateResponse = await updateUser(user.id, {
          user: {
            work_title: user.work_title ?? '',
            work_place: user.work_place ?? '',
            skill_ids: user.skill_ids ?? [],
            community_contribution_ids: user.community_contribution_ids ?? [],
            school: user.school ?? '',
            school_type: user.school_type ?? '',
          },
        })
        if (userUpdateResponse?.status !== 200) {
          console.warn('[Account edit] updateUser (profile extras) non-200:', userUpdateResponse)
        }
      } catch (err) {
        console.warn('[Account edit] updateUser (profile extras) failed:', err)
      }
    }

    if (email !== user.email) {
      const userInformationParams = {
        user: {
          email,
          password: emailPasswordConfirmation,
        },
      }
      const userInformationResponse = await updateUserInformation(
        user.id,
        userInformationParams,
      )
      if (
        userInformationResponse?.success === false &&
        !(userInformationResponse?.status === 200)
      ) {
        alert(t('users.edit.email_can_not_be_updated'))
        setEmail(user.email)
        setOpenPasswordConfirmationModal(false)
        setEmailPasswordConfirmation('')
      } else {
        const newUser = {
          ...user,
          email: email,
          email_verified_at: null,
          uid: email,
        }
        setUser(newUser)
        setOpenPasswordConfirmationModal(false)
        setEmailPasswordConfirmation('')
        updateEmailHeaders(email)
      }
    }


    if (
      user.nickname &&
      user.nickname !== currentUser?.nickname &&
      !nicknameTaken
    ) {
      try {
        const nicknameResponse = await updateUser(user.id, {
          user: { nickname: user.nickname.trim() },
        })
        if (nicknameResponse.status === 200) {
          const updatedUser = {
            ...user,
            nickname: user.nickname.trim(),
          }
          setUser(updatedUser)
          setCurrentUser(updatedUser)
          setNicknameTaken(false)
          setCheckingNickname(false)
        } else {
          alert(t('users.edit.nickname_can_not_be_updated') || 'Username could not be updated')
        }
      } catch (error) {
        alert(t('users.edit.nickname_can_not_be_updated') || 'Username could not be updated')
      }
    }

    setIsLoading(false)
  }, [
    user,
    setCurrentUser,
    fetchData,
    emailPasswordConfirmation,
    setUser,
    updateEmailHeaders,
    user?.email,
    email,
    currentUser,
    nicknameTaken,
    updateUser,
    t,
  ])

  useEffect(() => {
    fetchData()
    if (nameRef?.current) {
      nameRef.current.focus()
      nameRef.current.blur()
    }
    if (
      localStorage.getItem('force-verify-email') ||
      localStorage.getItem('new-account')
    ) {
      localStorage.removeItem('force-verify-email')
      localStorage.removeItem('new-account')
      fetchUser().then(() => {
        if (!user?.email_verified_at) {
          onEmailVerifyRequest()
        }
      })
    }
  }, [])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (nicknameCheckTimer.current) {
        clearTimeout(nicknameCheckTimer.current)
      }
    }
  }, [])

  const tryToSave = () => {
    if (user.email === email) {
      onSave()
    } else {
      setOpenPasswordConfirmationModal(true)
    }
  }

  const onSaveSocials = useCallback(async () => {
    if (!user?.id) return
    setIsSavingSocials(true)
    const userParams = {
      facebook_link: ensureAbsoluteUrl(user.facebook_link) || '',
      linkedin_link: ensureAbsoluteUrl(user.linkedin_link) || '',
      instagram_link: ensureAbsoluteUrl(user.instagram_link) || '',
      youtube_link: ensureAbsoluteUrl(user.youtube_link) || '',
      tiktok_link: ensureAbsoluteUrl(user.tiktok_link) || '',
      website_link: ensureAbsoluteUrl(user.website_link) || '',
    }
    try {
      const response = await updateUser(user.id, { user: userParams })
      if (response?.errors) {
        // eslint-disable-next-line no-alert
        alert(response.errors?.full_messages?.[0])
      } else {
        setHasSocialsChanged(false)
      }
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.errors?.full_messages?.[0] ?? 'Failed to save socials')
    }
    setIsSavingSocials(false)
  }, [user?.id, user?.facebook_link, user?.linkedin_link, user?.instagram_link, user?.youtube_link, user?.tiktok_link, user?.website_link, updateUser])

  const imageUserOnChange = async mediaAttachment => {
    const formData = new FormData()
    formData.append('media_attachment_id', mediaAttachment.id)
    const response = await updateAccount(formData)
    if (response.errors) {
      // eslint-disable-next-line no-alert
      alert(response.errors?.full_messages?.[0])
    } else {
      setUser({ ...user, media_attachment: { ...mediaAttachment } })
      setHasUserChanged(true)
    }
  }

  return (
    <>
      <Loading show={isLoading} />
      <div className="container">
        <div className="row">
          <h1 className="fs-1 pt-4">{t('users.edit.profile_settings')}</h1>
        </div>
      </div>
      <hr />
      <div className="container">
        <div className="row">
          <h1>{t('users.edit.user_profile')}</h1>
        </div>
        <div className="row">
          <div className="col">
            <AvatarImage
              imageUrl={
                user?.media_attachment?.thumbnail_url ??
                user?.media_attachment?.direct_upload_url ??
                '/images/default-user.png'
              }
              mediaAttachmentType="user"
              onChange={imageUserOnChange}
            />
          </div>
        </div>

        <div className="row form-row">
          <div className="form-group col-md-6">
            <label htmlFor="name">{t('devise.sessions.fullname')}</label>
            <input
              className="form-control"
              id="fullName"
              name="name"
              type="text"
              value={user?.name}
              onChange={e => {
                setUser({ ...user, name: e.target.value })
                setHasUserChanged(true)
              }}
              ref={nameRef}
            />
            {/* <div className="alert alert-danger hide">
                {t("users.edit.fullname_required")}
              </div> */}
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="email">
              {t('devise.sessions.email')}
              {!!user?.email_verified_at ? (
                <span className="verified-account">
                  {' '}
                  <Icon name={'user-check'} /> {t('users.edit.email_verified')}
                </span>
              ) : (
                <span
                  className="btn btn-link"
                  onClick={onEmailVerifyRequest}
                  role="presentation"
                >
                  <Icon name={'envelope'} />{' '}
                  {emailVerificationError
                    ? t('users.edit.email_verify_failed')
                    : t('users.edit.email_verify_prompt')}
                </span>
              )}
            </label>
            <input
              className="form-control"
              id="email"
              name="email"
              type="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value)
              }}
            />
            {/* {devise_mapping.confirmable? && resource.pending_reconfirmation? && (
              <div>{t('users.edit.pending_confirmation')} {resource.unconfirmed_email}</div>
            )} */}
          </div>
        </div>
        <div className="row form-row">
          <div className="form-group col-md-6">
            <label htmlFor="nickname">{t('devise.sessions.username')}</label>
            <div className="input-group mb-2">
              <div className="input-group-prepend">
                <div className="input-group-text">@</div>
              </div>
              <input
                className="form-control"
                id="nickname"
                name="nickname"
                type="text"
                value={user?.nickname || ''}
                onChange={e => onChangeNickname(e.target.value)}
                autoCapitalize="none"
              />
            </div>
            {checkingNickname && (
              <small className="form-text text-muted">
                Checking availability...
              </small>
            )}
            {nicknameTaken && !checkingNickname && user?.nickname && user.nickname !== currentUser?.nickname && (
              <small className="form-text text-danger">
                Username already taken
              </small>
            )}
            {!nicknameTaken && !checkingNickname && user?.nickname && user.nickname !== currentUser?.nickname && (
              <small className="form-text text-success">
                Username available
              </small>
            )}
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="phone">{t('users.edit.phone')}</label>
            <TelephoneInput
              className="form-control"
              country="US"
              initialValueFormat="national"
              international={false}
              onChange={phone => {
                setUser({ ...user, phone })
                setHasUserChanged(true)
              }}
              placeholder="(123) 456-7890"
              value={formatPhoneNumberForDisplay(user?.phone)}
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="autocomplete">{t('users.edit.location')}</label>
          <LocationSearch
            onChange={onLocationChange}
            placeholder={t('users.edit.search')}
            value={user?.location}
          />
          <br />
        </div>
        <div className="row form-row">
          <div className="col-md-6 form-group">
            <label htmlFor="birthDate">{t('users.edit.birthday')}</label>
            <DatePicker
              className="form-control"
              dateFormat={t('config.date_picker')}
              id="birthDate"
              maxDate={subYears(new Date(), 13)}
              name="birthday"
              placeholderText={t('users.edit.no_birthday')}
              selected={user?.birthday ? moment(user?.birthday).toDate() : null}
              shouldCloseOnSelect
              wrapperClassName="form-control"
              onChange={date => {
                setUser({
                  ...user,
                  birthday: date ? moment(date).format('YYYY-MM-DD') : null,
                })
                setHasUserChanged(true)
              }}
            />
          </div>
          <div className="col-md-6 form-group">
            <label htmlFor="tShirt">{t('users.edit.t_shirt')}</label>
            <select
              name="T-Shirt"
              id="tShirt"
              className="form-control"
              onChange={({ target }) => {
                setUser({ ...user, shirt_size_id: target.value })
                setHasUserChanged(true)
              }}
            >
              <option selected={!user.shirt_size} value="">
                {t('users.edit.no_gender')}
              </option>
              {shirtSizes ? (
                shirtSizes.map(shirtSize => {
                  return (
                    <option
                      key={shirtSize.id}
                      selected={user.shirt_size?.id === shirtSize.id}
                      value={shirtSize.id}
                    >
                      {shirtSize[`${locale}_name`]}
                    </option>
                  )
                })
              ) : (
                <option disabled>loading...</option>
              )}
            </select>
          </div>
        </div>
        <div className="row form-row">
          <div className="form-group col-md-6">
            <label htmlFor="gender">{t('users.edit.gender')}</label>
            <select
              name="gender"
              id="gender"
              className="form-control"
              onChange={e => {
                setUser({ ...user, gender: e.target.value })
                setHasUserChanged(true)
              }}
            >
              <option selected={!user?.gender || user?.gender === ''} value="">
                {t('users.edit.no_gender')}
              </option>
              <option
                selected={user?.gender === 'Male' || user?.gender === 'Hombre'}
                value="Male"
              >
                {t('users.edit.male')}
              </option>
              <option
                selected={user?.gender === 'Female' || user?.gender === 'Mujer'}
                value="Female"
              >
                {t('users.edit.female')}
              </option>
              <option
                selected={user?.gender === 'Other' || user?.gender === 'Otro'}
                value="Other"
              >
                {t('users.edit.other_gender')}
              </option>
            </select>
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="language">{t('users.edit.language')}</label>
            <select
              name="language"
              id="language"
              className="form-control"
              onChange={e => {
                setUser({ ...user, language: e.target.value })
                setHasUserChanged(true)
              }}
            >
              <option selected={user?.language === 'en'} value="en">
                {t('users.edit.english')}
              </option>
              <option selected={user?.language === 'es'} value="es">
                {t('users.edit.spanish')}
              </option>
            </select>
          </div>
        </div>
        <div className="row form-row">
          <div className="form-group col-md-6">
            <label htmlFor="work_title">{t('users.edit.work_title')}</label>
            <input
              className="form-control"
              id="work_title"
              name="work_title"
              type="text"
              value={user?.work_title || ''}
              onChange={e => {
                setUser({ ...user, work_title: e.target.value })
                setHasUserChanged(true)
              }}
            />
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="work_place">{t('users.edit.work_place')}</label>
            <input
              className="form-control"
              id="work_place"
              name="work_place"
              type="text"
              value={user?.work_place || ''}
              onChange={e => {
                setUser({ ...user, work_place: e.target.value })
                setHasUserChanged(true)
              }}
            />
          </div>
        </div>
        <div className="row form-row">
          <div className="form-group col-md-6">
            <label>{t('users.edit.skills')}</label>
            <small className="form-text text-muted d-block mb-1">
              {t('users.edit.select_up_to_three')}
            </small>
            <Select
              isMulti
              maxMenuHeight={220}
              placeholder={t('users.edit.select_up_to_three')}
              options={skillsList.map(skill => {
                const lang = i18n.language?.startsWith('es') ? 'es' : 'en'
                const label = skill[`${lang}_name`] ?? skill.name ?? skill.en_name ?? skill.es_name ?? `Skill ${skill.id}`
                return { value: skill.id, label }
              })}
              value={(user?.skill_ids ?? [])
                .map(id => skillsList.find(s => s.id === id))
                .filter(Boolean)
                .map(skill => {
                  const lang = i18n.language?.startsWith('es') ? 'es' : 'en'
                  const label = skill[`${lang}_name`] ?? skill.name ?? skill.en_name ?? skill.es_name ?? `Skill ${skill.id}`
                  return { value: skill.id, label }
                })}
              onChange={selected => {
                const next = (selected ?? []).map(o => o.value).slice(0, 3)
                setUser({ ...user, skill_ids: next })
                setHasUserChanged(true)
              }}
              noOptionsMessage={() => t('users.edit.no_options')}
              classNamePrefix="account-select"
            />
          </div>
          <div className="form-group col-md-6">
            <label>{t('users.edit.community_contributions')}</label>
            <small className="form-text text-muted d-block mb-1">
              {t('users.edit.select_up_to_three')}
            </small>
            <Select
              isMulti
              maxMenuHeight={220}
              placeholder={t('users.edit.select_up_to_three')}
              options={communityContributionsList.map(cc => {
                const lang = i18n.language?.startsWith('es') ? 'es' : 'en'
                const label = cc[`${lang}_name`] ?? cc.name ?? cc.en_name ?? cc.es_name ?? `Contribution ${cc.id}`
                return { value: cc.id, label }
              })}
              value={(user?.community_contribution_ids ?? [])
                .map(id => communityContributionsList.find(c => c.id === id))
                .filter(Boolean)
                .map(cc => {
                  const lang = i18n.language?.startsWith('es') ? 'es' : 'en'
                  const label = cc[`${lang}_name`] ?? cc.name ?? cc.en_name ?? cc.es_name ?? `Contribution ${cc.id}`
                  return { value: cc.id, label }
                })}
              onChange={selected => {
                const next = (selected ?? []).map(o => o.value).slice(0, 3)
                setUser({ ...user, community_contribution_ids: next })
                setHasUserChanged(true)
              }}
              noOptionsMessage={() => t('users.edit.no_options')}
              classNamePrefix="account-select"
            />
          </div>
        </div>
        <div className="row form-row">
          <div className="form-group col-md-6">
            <label htmlFor="school">{t('users.edit.school')}</label>
            <LocationSearch
              onChange={onSchoolChange}
              placeholder={t('users.edit.search')}
              value={user?.school}
            />
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="school_type">{t('users.edit.school_type')}</label>
            <select
              name="school_type"
              id="school_type"
              className="form-control"
              value={user?.school_type ?? ''}
              onChange={e => {
                setUser({ ...user, school_type: e.target.value })
                setHasUserChanged(true)
              }}
            >
              {/* there is an option related to the gender drop down for non-specified but I made a new one for schools, we should review translation files and see if there's a consistent pattern of reusing it or not. */}
              <option value="">{t('users.edit.school_type_none')}</option>
              <option value="Intermediate School">{t('users.edit.school_type_intermediate')}</option>
              <option value="High School">{t('users.edit.school_type_high_school')}</option>
              <option value="College">{t('users.edit.school_type_college')}</option>
            </select>
            {user?.school?.trim() && !user?.school_type?.trim() && (
              <small className="form-text text-muted">
                {t('users.edit.school_type_required')}
              </small>
            )}
          </div>
        </div>
        <input
          className="btn btn-primary"
          id="settings-form-submit"
          onClick={tryToSave}
          type="submit"
          value={t('users.edit.save')}
          disabled={
            (!hasUserChanged && email === user.email) ||
            (user?.nickname &&
              user.nickname !== currentUser?.nickname &&
              (checkingNickname || nicknameTaken)) ||
            (user?.school?.trim() && !user?.school_type?.trim())
          }
        />
      </div>
      <hr />

      <div className="container">
        <div className="row">
          <h1>{t('users.edit.socials')}</h1>
        </div>
        <div className="row form-row">
          <div className="form-group col-md-6">
            <label htmlFor="facebook_link">{t('users.edit.facebook_link')}</label>
            <input
              className="form-control"
              id="facebook_link"
              name="facebook_link"
              type="url"
              placeholder="https://facebook.com/..."
              value={user?.facebook_link || ''}
              onChange={e => {
                setUser({ ...user, facebook_link: e.target.value })
                setHasSocialsChanged(true)
              }}
            />
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="linkedin_link">{t('users.edit.linkedin_link')}</label>
            <input
              className="form-control"
              id="linkedin_link"
              name="linkedin_link"
              type="url"
              placeholder="https://linkedin.com/in/..."
              value={user?.linkedin_link || ''}
              onChange={e => {
                setUser({ ...user, linkedin_link: e.target.value })
                setHasSocialsChanged(true)
              }}
            />
          </div>
        </div>
        <div className="row form-row">
          <div className="form-group col-md-6">
            <label htmlFor="instagram_link">{t('users.edit.instagram_link')}</label>
            <input
              className="form-control"
              id="instagram_link"
              name="instagram_link"
              type="url"
              placeholder="https://instagram.com/..."
              value={user?.instagram_link || ''}
              onChange={e => {
                setUser({ ...user, instagram_link: e.target.value })
                setHasSocialsChanged(true)
              }}
            />
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="youtube_link">{t('users.edit.youtube_link')}</label>
            <input
              className="form-control"
              id="youtube_link"
              name="youtube_link"
              type="url"
              placeholder="https://youtube.com/..."
              value={user?.youtube_link || ''}
              onChange={e => {
                setUser({ ...user, youtube_link: e.target.value })
                setHasSocialsChanged(true)
              }}
            />
          </div>
        </div>
        <div className="row form-row">
          <div className="form-group col-md-6">
            <label htmlFor="tiktok_link">{t('users.edit.tiktok_link')}</label>
            <input
              className="form-control"
              id="tiktok_link"
              name="tiktok_link"
              type="url"
              placeholder="https://tiktok.com/@..."
              value={user?.tiktok_link || ''}
              onChange={e => {
                setUser({ ...user, tiktok_link: e.target.value })
                setHasSocialsChanged(true)
              }}
            />
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="website_link">{t('users.edit.website_link')}</label>
            <input
              className="form-control"
              id="website_link"
              name="website_link"
              type="url"
              placeholder="https://..."
              value={user?.website_link || ''}
              onChange={e => {
                setUser({ ...user, website_link: e.target.value })
                setHasSocialsChanged(true)
              }}
            />
          </div>
        </div>
        <button
          className="btn btn-primary"
          type="button"
          onClick={onSaveSocials}
          disabled={!hasSocialsChanged || isSavingSocials}
        >
          {isSavingSocials ? t('users.edit.saving') : t('users.edit.save')}
        </button>
      </div>
      <hr />

      <CausePicker />
      <hr />
      <DefaultCharity />
      <hr />
      {hasBetaFeature('civ_wallet') ? (
        <>
          <CivWallet
            data={user}
            setData={setUser}
            locale={locale}
            updateData={updateUser}
            origin={'user'}
          />
          <hr />
        </>
      ) : null}

      <div className="container">
        <div className="row">
          <h1>
            <Icon name="lock-alt" className="business-title-icon" />
            {t('users.edit.update_pw')}
          </h1>
        </div>
        <div className="row">
          <div className="form-group col-md-6">
            <label htmlFor="inputNewPassword">{t('users.edit.new_pw')}</label>
            <PasswordInput
              onChange={val => setNewPassword(val)}
              showPasswordToggle
              value={newPassword || ''}
              autofocus={false}
            />
            <small id="newPasswordHelp" className="form-text text-muted">
              {t('users.edit.pw_requirements')} _!@%&#x26;()*&#x27;&#x27;.
            </small>
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="confirmPassword">
              {t('users.edit.confirm_pw')}
            </label>
            <PasswordInput
              onChange={val => setConfirmPassword(val)}
              showPasswordToggle
              value={confirmPassword || ''}
              autofocus={false}
            />
          </div>
        </div>
        <button
          className="btn btn-primary"
          onClick={onSaveNewPassword}
          style={{ marginBottom: '50px' }}
          type="submit"
          disabled={
            !newPassword ||
            !confirmPassword ||
            newPassword !== confirmPassword ||
            newPassword.length < 6
          }
        >
          {t('users.edit.save')}
        </button>
      </div>

      <Modal show={waitingForEmailVerification} onHide={onEmailVerifyCancel}>
        <Modal.Header closeButton>
          <Modal.Title>{t('users.edit.email_verify_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            {t('users.edit.email_verify_message', { email: user.email })}
          </div>
          <br />
          {emailVerificationError && (
            <Alert
              variant="danger"
              onClose={() => setEmailVerificationError(false)}
              dismissible
            >
              <p>{t('users.edit.email_verify_error_alert')}</p>
            </Alert>
          )}
          {localStorage.getItem('verify-email-message') && (
            <Alert
              variant="warning"
              onClose={() => localStorage.removeItem('verify-email-message')}
              dismissible
            >
              <p>{localStorage.getItem('verify-email-message')}</p>
            </Alert>
          )}
          <input
            className="form-control"
            inputMode="numeric"
            type="text"
            placeholder={t('users.edit.email_verify_placeholder')}
            onChange={e => setEmailVerificationCode(e.target.value)}
            value={emailVerificationCode}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onEmailVerifyCancel}>
            {t('event.delete_modal_cancel')}
          </Button>
          <Button variant="primary" onClick={onEmailVerifyCode}>
            {t('users.edit.email_verify')}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={openPasswordConfirmationModal}
        onHide={() => setOpenPasswordConfirmationModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t('users.edit.email_verify_password_title')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            {t('users.edit.email_verify_password_description', {
              email: user.email,
            })}
          </div>
          <br />
          <label htmlFor="emailPasswordConfirmation">
            {t('users.edit.password_confirmation')}
          </label>
          <PasswordInput
            className="form-control"
            name={'emailPasswordConfirmation'}
            id={'emailPasswordConfirmation'}
            type="text"
            onChange={value => setEmailPasswordConfirmation(value)}
            value={emailPasswordConfirmation}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => onSave()}
            disabled={!emailPasswordConfirmation}
          >
            {t('users.edit.save')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setOpenPasswordConfirmationModal(false)}
          >
            {t('event_card.close')}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={emailVerificationMaxPerDay}
        onHide={() => setEmailVerificationMaxPerDay(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('users.edit.email_verify_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <p>{t('users.edit.email_verify_max_day')}</p>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setEmailVerificationMaxPerDay(false)}
          >
            {t('event_card.close')}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={emailVerificationMaxPerMonth}
        onHide={() => setEmailVerificationMaxPerMonth(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('users.edit.email_verify_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <p>{t('users.edit.email_verify_max_month')}</p>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setEmailVerificationMaxPerMonth(false)}
          >
            {t('event_card.close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
